import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon/Icon';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
export const ACCEPTED_EXTENSIONS_LABEL = 'PNG, JPEG, WebP ou PDF';

export interface ReceiptDropzoneProps {
  /** Appelé avec le fichier choisi, ou un message d'erreur si le type est refusé. */
  onFileAccepted: (file: File) => void;
  onFileRejected: (message: string) => void;
}

function isAccepted(file: File): boolean {
  return ACCEPTED_TYPES.includes(file.type);
}

/**
 * Zone de dépôt drag & drop (avec repli via sélecteur de fichier).
 * Le fichier retenu est remonté au parent ; la validation du type est faite ici.
 */
export function ReceiptDropzone({ onFileAccepted, onFileRejected }: ReceiptDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (isAccepted(file)) {
      onFileAccepted(file);
    } else {
      onFileRejected(
        `Format non pris en charge. Fichiers acceptés : ${ACCEPTED_EXTENSIONS_LABEL}.`,
      );
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFile(event.dataTransfer.files[0]);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
    // Permet de re-sélectionner le même fichier.
    event.target.value = '';
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Déposer un ticket ou une facture"
      onClick={() => inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        'flex min-h-56 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 text-center transition-colors',
        isDragging
          ? 'border-emerald-500 bg-emerald-50'
          : 'border-slate-300 bg-white hover:border-emerald-400 hover:bg-slate-50',
      )}
    >
      <span
        className={cn(
          'flex size-12 items-center justify-center rounded-full transition-colors',
          isDragging ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500',
        )}
      >
        <Icon name="upload" size={22} />
      </span>
      <p className="text-sm font-medium text-slate-700">
        Glissez-déposez votre ticket ou facture ici
      </p>
      <p className="text-xs text-slate-500">
        ou cliquez pour parcourir — {ACCEPTED_EXTENSIONS_LABEL}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        className="hidden"
        onChange={handleChange}
        data-testid="receipt-file-input"
      />
    </div>
  );
}
